// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierPaint.js   ·   EVERY SURFACE ON THE BOARDWALK
// ────────────────────────────────────────────────────────────────────
// Painted, never loaded. Every texture here is a cached canvas, seeded,
// so the pier is byte-identical on every client with zero assets.
//
// ⚑ NEUTRAL WHERE A MATERIAL TINTS IT. Clapboard, shingles and stone are
// painted in greys so ONE texture serves twenty-three buildings and the
// building's colour rides on `material.color`. Signs, awnings and rifts
// carry their own colour because text and stripes cannot be tinted
// after the fact.
// ════════════════════════════════════════════════════════════════════

import * as THREE from "three";
import { canvasTex, mulberry32, FONT_GEN, TEX } from "./pierTex";

const FONT = "Cinzel, 'Palatino Linotype', serif";

const repeat = (t, x, y) => {
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(x, y);
  return t;
};

// ── THE DECK — planks running ACROSS the walk, as a real boardwalk's do.
// One texture covers 4×4 tiles with world-space UVs, so seams run on
// unbroken across every tile instead of restarting per square.
export const deckTex = () => canvasTex("bw-deck", 1024, (ctx, S) => {
  const R = mulberry32(0xDEC4);
  const N = 32;                       // 8 boards a tile
  const bw = S / N;
  for (let i = 0; i < N; i++) {
    const tone = 0.82 + R() * 0.3;
    const base = [96, 72, 46].map((c) => Math.round(c * tone));
    ctx.fillStyle = `rgb(${base})`;
    ctx.fillRect(i * bw, 0, bw, S);
    // grain
    for (let g = 0; g < 26; g++) {
      ctx.strokeStyle = `rgba(28,18,9,${0.06 + R() * 0.14})`;
      ctx.lineWidth = 0.6 + R() * 1.2;
      const x = i * bw + 2 + R() * (bw - 4);
      ctx.beginPath(); ctx.moveTo(x, R() * S); ctx.lineTo(x + (R() - 0.5) * 3, R() * S); ctx.stroke();
    }
    // gap between boards
    ctx.fillStyle = "rgba(14,9,5,0.85)";
    ctx.fillRect(i * bw, 0, 2, S);
    // butt joints, staggered, each with its nails
    let y = R() * S * 0.5;
    while (y < S) {
      ctx.fillStyle = "rgba(14,9,5,0.8)";
      ctx.fillRect(i * bw, y, bw, 2);
      ctx.fillStyle = "#3a2c1c";
      ctx.fillRect(i * bw + 5, y + 5, 3, 3);
      ctx.fillRect(i * bw + bw - 8, y + 5, 3, 3);
      y += S * (0.3 + R() * 0.45);
    }
  }
  // weathering — salt-bleached patches and wet darkening
  for (let k = 0; k < 90; k++) {
    const x = R() * S, y = R() * S, r = 20 + R() * 70;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    const light = R() < 0.4;
    g.addColorStop(0, light ? "rgba(200,180,150,0.07)" : "rgba(10,6,2,0.10)");
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g; ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
});

// ── dark structural timber: fascia, piles, braces, trim ──
export const timberTex = () => repeat(canvasTex("bw-timber", 256, (ctx, S) => {
  const R = mulberry32(0x71B3);
  ctx.fillStyle = "#6a543c"; ctx.fillRect(0, 0, S, S);
  for (let i = 0; i < 160; i++) {
    ctx.strokeStyle = `rgba(20,12,6,${0.08 + R() * 0.2})`;
    ctx.lineWidth = 0.5 + R() * 1.5;
    const y = R() * S;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.bezierCurveTo(S * 0.3, y + (R() - 0.5) * 8, S * 0.7, y + (R() - 0.5) * 8, S, y); ctx.stroke();
  }
}), 1, 1);

// ── CLAPBOARD — neutral; the building's colour is the material's ──
export const clapTex = () => repeat(canvasTex("bw-clap", 256, (ctx, S) => {
  const R = mulberry32(0xC1AB);
  const rows = 10, h = S / rows;
  for (let r = 0; r < rows; r++) {
    const v = 205 + Math.round(R() * 30);
    const g = ctx.createLinearGradient(0, r * h, 0, (r + 1) * h);
    g.addColorStop(0, `rgb(${v},${v},${v})`);
    g.addColorStop(0.85, `rgb(${v - 30},${v - 30},${v - 30})`);
    g.addColorStop(1, "rgb(70,70,70)");
    ctx.fillStyle = g; ctx.fillRect(0, r * h, S, h);
    for (let k = 0; k < 14; k++) {
      ctx.fillStyle = `rgba(0,0,0,${0.03 + R() * 0.07})`;
      ctx.fillRect(R() * S, r * h + R() * h, 20 + R() * 60, 1);
    }
  }
}), 1, 1);

// ── SHINGLES — neutral, staggered rows ──
export const shingleTex = () => repeat(canvasTex("bw-shingle", 256, (ctx, S) => {
  const R = mulberry32(0x5A1E);
  ctx.fillStyle = "#2a2a2a"; ctx.fillRect(0, 0, S, S);
  const rows = 8, h = S / rows, w = S / 6;
  for (let r = 0; r < rows; r++) {
    for (let c = -1; c < 7; c++) {
      const x = c * w + (r % 2 ? w / 2 : 0);
      const v = 120 + Math.round(R() * 60);
      ctx.fillStyle = `rgb(${v},${v},${v})`;
      ctx.beginPath();
      ctx.moveTo(x + 2, r * h);
      ctx.lineTo(x + w - 2, r * h);
      ctx.lineTo(x + w - 3, r * h + h - 3);
      ctx.quadraticCurveTo(x + w / 2, r * h + h + 2, x + 3, r * h + h - 3);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = "rgba(0,0,0,0.25)";
      ctx.fillRect(x + 2, r * h + h - 5, w - 4, 3);
    }
  }
}), 1, 1);

// ── DRESSED STONE — the Well, the Hall ──
export const stoneTex = () => repeat(canvasTex("bw-stone", 256, (ctx, S) => {
  const R = mulberry32(0x5709);
  ctx.fillStyle = "#3b3640"; ctx.fillRect(0, 0, S, S);
  const rows = 6, h = S / rows;
  for (let r = 0; r < rows; r++) {
    let x = r % 2 ? -S / 8 : 0;
    while (x < S) {
      const w = S / 4 * (0.7 + R() * 0.6);
      const v = 150 + Math.round(R() * 50);
      ctx.fillStyle = `rgb(${v},${v - 4},${v + 6})`;
      ctx.fillRect(x + 2, r * h + 2, w - 4, h - 4);
      for (let k = 0; k < 30; k++) {
        ctx.fillStyle = `rgba(0,0,0,${R() * 0.12})`;
        ctx.fillRect(x + R() * w, r * h + R() * h, 2, 2);
      }
      x += w;
    }
  }
}), 1, 1);

// ── A LIT WINDOW — the emissive map. Warm white; the building tints it. ──
export const windowTex = (boarded) => canvasTex(boarded ? "bw-win-board" : "bw-win-lit", 128, (ctx, S) => {
  const R = mulberry32(boarded ? 0xB0A2 : 0x1175);
  // frame
  ctx.fillStyle = "#050308"; ctx.fillRect(0, 0, S, S);
  if (!boarded) {
    const g = ctx.createRadialGradient(S / 2, S * 0.7, 4, S / 2, S * 0.6, S * 0.75);
    g.addColorStop(0, "#fff4d8");
    g.addColorStop(0.5, "#d9a860");
    g.addColorStop(1, "#3a2410");
    ctx.fillStyle = g; ctx.fillRect(8, 8, S - 16, S - 16);
    // shelves and shapes inside — somebody's shop
    ctx.fillStyle = "rgba(40,22,10,0.55)";
    ctx.fillRect(8, S * 0.62, S - 16, 5);
    for (let i = 0; i < 6; i++) ctx.fillRect(14 + R() * (S - 36), S * 0.62 - 6 - R() * 14, 6 + R() * 8, 6 + R() * 14);
    // mullions
    ctx.fillStyle = "#0a0604";
    ctx.fillRect(S / 2 - 3, 8, 6, S - 16);
    ctx.fillRect(8, S / 2 - 3, S - 16, 6);
  } else {
    // ⚠ NOT BOARDED. Twenty-one of twenty-three wings are "coming soon",
    // and X-boards over every window turned the pier into a ghost town.
    // A shop getting ready to open has its curtains drawn and a light on
    // behind them — a promise, not a ruin.
    // ⚠ And not crossed curtains either — two drapes tied back read as an
    // X from down the pier, which is the boards again. A lit room behind a
    // half-drawn linen blind, with the mullions over it, reads as a shop
    // that is simply not open yet.
    const g = ctx.createRadialGradient(S / 2, S * 0.72, 2, S / 2, S * 0.7, S * 0.62);
    g.addColorStop(0, "#ffe6b8"); g.addColorStop(0.55, "#c08a4c"); g.addColorStop(1, "#40240e");
    ctx.fillStyle = g; ctx.fillRect(8, 8, S - 16, S - 16);
    const blind = ctx.createLinearGradient(0, 8, 0, S * 0.5);
    blind.addColorStop(0, "#e8d2a8"); blind.addColorStop(1, "#b89464");
    ctx.fillStyle = blind; ctx.fillRect(8, 8, S - 16, S * 0.46);
    for (let y = 16; y < S * 0.5; y += 7) {
      ctx.fillStyle = "rgba(90,60,30,0.18)"; ctx.fillRect(8, y, S - 16, 2);
    }
    ctx.fillStyle = "#6a4a28"; ctx.fillRect(8, S * 0.5, S - 16, 5);       // the blind's bar
    ctx.fillStyle = "#0a0604";
    ctx.fillRect(S / 2 - 3, 8, 6, S - 16);
    ctx.fillRect(8, S / 2 - 3, S - 16, 6);
  }
});

// Boards nailed across a closed shop's window or door — its own albedo.
export const boardsTex = () => canvasTex("bw-boards", 128, (ctx, S) => {
  ctx.clearRect(0, 0, S, S);
  const plank = (ang, c) => {
    ctx.save(); ctx.translate(S / 2, S / 2); ctx.rotate(ang);
    ctx.fillStyle = c; ctx.fillRect(-S * 0.75, -11, S * 1.5, 22);
    ctx.fillStyle = "rgba(0,0,0,0.3)"; ctx.fillRect(-S * 0.75, 8, S * 1.5, 3);
    ctx.fillStyle = "#2a2018"; ctx.fillRect(-S * 0.42, -3, 5, 5); ctx.fillRect(S * 0.36, -3, 5, 5);
    ctx.restore();
  };
  plank(0.62, "#6b5236"); plank(-0.62, "#5c4630");
});

// ── AN AWNING — the building's colour and cream, scalloped hem ──
export const awningTex = (color) => canvasTex(`bw-awn|${color}`, 256, (ctx, S) => {
  ctx.clearRect(0, 0, S, S);
  const n = 8, w = S / n;
  for (let i = 0; i < n; i++) {
    ctx.fillStyle = i % 2 ? "#e9dcc0" : color;
    ctx.fillRect(i * w, 0, w, S * 0.78);
    ctx.beginPath(); ctx.arc(i * w + w / 2, S * 0.78, w / 2, 0, Math.PI);
    ctx.fill();
  }
  const g = ctx.createLinearGradient(0, 0, 0, S * 0.8);
  g.addColorStop(0, "rgba(255,255,255,0.18)"); g.addColorStop(1, "rgba(0,0,0,0.28)");
  ctx.globalCompositeOperation = "source-atop";
  ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
  ctx.globalCompositeOperation = "source-over";
}, 128);

// ── THE SIGNBOARD — a lacquered board, gilt border, bulbs, the name ──
// Wide canvas (1024×200) so the name is legible from down the pier.
export function signTex(label, color, soon) {
  const key = `bw-sign|${FONT_GEN}|${label}|${color}|${soon ? 1 : 0}`;
  if (TEX.has(key)) return TEX.get(key);
  const W = 1024, H = 200;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const ctx = cv.getContext("2d");
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#1b1226"); g.addColorStop(1, "#0a0612");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = soon ? "#6a5a40" : "#c9a84c"; ctx.lineWidth = 8;
  ctx.strokeRect(10, 10, W - 20, H - 20);
  ctx.strokeStyle = (soon ? "#6a5a40" : color) + "88"; ctx.lineWidth = 3;
  ctx.strokeRect(24, 24, W - 48, H - 48);
  // bulbs
  if (!soon) {
    for (let i = 0; i < 26; i++) {
      const x = 40 + (i * (W - 80)) / 25;
      for (const y of [12, H - 12]) {
        ctx.beginPath(); ctx.arc(x, y, 6, 0, 7);
        ctx.fillStyle = i % 2 ? "#fff0b8" : color;
        ctx.shadowColor = ctx.fillStyle; ctx.shadowBlur = 14; ctx.fill(); ctx.shadowBlur = 0;
      }
    }
  }
  // the name — fit to the board
  let size = 96;
  const text = label.toUpperCase();
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  do { ctx.font = `900 ${size}px ${FONT}`; size -= 4; } while (ctx.measureText(text).width > W - 120 && size > 30);
  ctx.fillStyle = soon ? "#9a8a7a" : color;
  if (!soon) { ctx.shadowColor = color; ctx.shadowBlur = 30; }
  ctx.fillText(text, W / 2, H / 2 + 4);
  ctx.shadowBlur = 0;
  ctx.fillStyle = soon ? "#b9ad96" : "#fff6dc";
  ctx.globalAlpha = soon ? 0.9 : 0.55;
  ctx.fillText(text, W / 2, H / 2 + 4);
  ctx.globalAlpha = 1;
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  TEX.set(key, t);
  return t;
}

// ── A NEON — OPEN hums, SOON sulks ──
export function neonTex(text, color) {
  return canvasTex(`bw-neon|${FONT_GEN}|${text}|${color}`, 256, (ctx, W, H) => {
    ctx.clearRect(0, 0, W, H);
    ctx.font = `700 54px ${FONT}`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.strokeStyle = color; ctx.lineWidth = 3;
    ctx.shadowColor = color; ctx.shadowBlur = 18;
    ctx.strokeRect(14, 14, W - 28, H - 28);
    ctx.fillStyle = color; ctx.fillText(text, W / 2, H / 2 + 3);
    ctx.shadowBlur = 0; ctx.fillStyle = "#ffffffcc"; ctx.fillText(text, W / 2, H / 2 + 3);
  }, 104);
}

// ── THE HANGING SHINGLE — the wing's own glyph on a little board ──
export function glyphTex(icon, color) {
  return canvasTex(`bw-glyph|${icon}|${color}`, 128, (ctx, S) => {
    ctx.fillStyle = "#120b1c"; ctx.fillRect(0, 0, S, S);
    ctx.strokeStyle = color; ctx.lineWidth = 6; ctx.strokeRect(6, 6, S - 12, S - 12);
    ctx.font = `72px "Segoe UI Emoji","Apple Color Emoji","Noto Color Emoji",${FONT}`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillStyle = color;
    ctx.fillText(icon, S / 2, S / 2 + 5);
  });
}

// ── THE RIFT — what you see through a wing's open door ──
export const riftTex = (color) => canvasTex(`bw-rift|${color}`, 256, (ctx, S) => {
  const c = S / 2;
  const g = ctx.createRadialGradient(c, c, 0, c, c, c);
  g.addColorStop(0, "#ffffff");
  g.addColorStop(0.18, color);
  g.addColorStop(0.62, color + "55");
  g.addColorStop(1, "#00000000");
  ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
  ctx.globalCompositeOperation = "lighter";
  const R = mulberry32(0x21F7);
  for (let i = 0; i < 9; i++) {
    const r = S * (0.12 + i * 0.045);
    const a0 = R() * 6.28, span = 0.8 + R() * 1.8;
    ctx.beginPath(); ctx.arc(c, c, r, a0, a0 + span);
    ctx.strokeStyle = (i % 2 ? "#ffffff" : color) + "66";
    ctx.lineWidth = 3 + R() * 5; ctx.stroke();
  }
  ctx.globalCompositeOperation = "source-over";
});

// ── A SOFT DOT — every bulb and lantern halo on the pier, in one draw ──
export const dotTex = () => canvasTex("bw-dot", 64, (ctx, S) => {
  const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.22, "rgba(255,255,255,0.75)");
  g.addColorStop(0.5, "rgba(255,255,255,0.18)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
});

// ── A POOL OF LIGHT on the planks — painted, never lit ──
export const poolTex = () => canvasTex("bw-pool", 128, (ctx, S) => {
  const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  g.addColorStop(0.00, "rgba(255,215,140,0.85)");
  g.addColorStop(0.35, "rgba(255,196,110,0.34)");
  g.addColorStop(0.70, "rgba(210,150,80,0.10)");
  g.addColorStop(1.00, "rgba(0,0,0,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
});

// ── A SOFT SHADOW under anything that stands on the deck ──
export const shadowTex = () => canvasTex("bw-shadow", 128, (ctx, S) => {
  const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  g.addColorStop(0, "rgba(0,0,0,0.75)");
  g.addColorStop(0.6, "rgba(0,0,0,0.3)");
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
});

// ── A SHUT SHOP DOOR — dark oak panels, a brass knob, and a plaque that
// says it is coming. Replaces the X of planks a "coming soon" door wore.
export const doorTex = () => canvasTex(`bw-door|${FONT_GEN}`, 128, (ctx, W, H) => {
  const R = mulberry32(0xD002);
  ctx.fillStyle = "#4a3222"; ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 90; i++) {
    ctx.strokeStyle = `rgba(20,10,4,${0.08 + R() * 0.2})`;
    ctx.beginPath(); const x = R() * W; ctx.moveTo(x, 0); ctx.lineTo(x + (R() - 0.5) * 4, H); ctx.stroke();
  }
  const panel = (x, y, w, h) => {
    ctx.fillStyle = "rgba(0,0,0,0.28)"; ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = "rgba(255,220,170,0.18)"; ctx.lineWidth = 2; ctx.strokeRect(x + 3, y + 3, w - 6, h - 6);
  };
  panel(12, 96, 46, 64); panel(70, 96, 46, 64); panel(12, 172, 46, 72); panel(70, 172, 46, 72);
  // the plaque
  ctx.fillStyle = "#1a1026"; ctx.fillRect(14, 22, W - 28, 56);
  ctx.strokeStyle = "#c9a84c"; ctx.lineWidth = 3; ctx.strokeRect(14, 22, W - 28, 56);
  ctx.fillStyle = "#f0d060"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.font = "700 15px Cinzel, 'Palatino Linotype', serif"; ctx.fillText("OPENING", W / 2, 40);
  ctx.font = "900 20px Cinzel, 'Palatino Linotype', serif"; ctx.fillText("SOON", W / 2, 62);
  // brass knob
  ctx.beginPath(); ctx.arc(W - 20, 168, 5, 0, 7); ctx.fillStyle = "#d8b060"; ctx.fill();
}, 256);
